import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';

const CommissionRuleForm = ({ onSubmit, onCancel, loading = false }) => {
  const { t } = useTranslation();
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      product_type: '',
      percentage_rate: '',
      fixed_amount: '',
      min_policy_amount: '',
      max_policy_amount: '',
      priority: '1',
      applies_to_new_business: true,
      applies_to_renewals: false
    }
  });

  const PRODUCT_TYPES = ['Auto', 'Casa', 'Comercial', 'All'];
  const PRIORITIES = ['1', '2', '3', '4', '5'];

  const onFormSubmit = (data) => {
    const transformedData = {
      ...data,
      percentage_rate: parseFloat(data.percentage_rate) || 0,
      fixed_amount: parseFloat(data.fixed_amount) || 0,
      min_policy_amount: parseFloat(data.min_policy_amount) || 0,
      max_policy_amount: parseFloat(data.max_policy_amount) || 0,
      priority: parseInt(data.priority) || 1
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div>
        <Label htmlFor="name">{t('commissions.ruleName')}*</Label>
        <input
          {...register('name', { required: 'Rule name is required' })}
          id="name"
          type="text"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder={t('commissions.ruleName')}
        />
        {errors.name && (
          <span className="text-sm text-red-500">{errors.name.message}</span>
        )}
      </div>

      <div>
        <Label htmlFor="description">{t('commissions.description')}</Label>
        <Textarea
          {...register('description')}
          id="description"
          rows={3}
          className="resize-none"
          placeholder={t('commissions.description')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="product_type">{t('commissions.productType')}*</Label>
          <Select
            value={watch('product_type')}
            onValueChange={(value) => setValue('product_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('commissions.productType')} />
            </SelectTrigger>
            <SelectContent>
              {PRODUCT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!watch('product_type') && (
            <span className="text-sm text-red-500">Product type is required</span>
          )}
        </div>
        <div>
          <Label htmlFor="priority">{t('commissions.priority')}</Label>
          <Select
            value={watch('priority')}
            onValueChange={(value) => setValue('priority', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('commissions.priority')} />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((priority) => (
                <SelectItem key={priority} value={priority}>
                  {priority}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="percentage_rate">{t('commissions.percentageRate')} (%)</Label>
          <input
            {...register('percentage_rate')}
            id="percentage_rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('commissions.percentageRate')}
          />
        </div>
        <div>
          <Label htmlFor="fixed_amount">{t('commissions.fixedAmount')} ($)</Label>
          <input
            {...register('fixed_amount')}
            id="fixed_amount"
            type="number"
            step="0.01"
            min="0"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('commissions.fixedAmount')}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="min_policy_amount">{t('commissions.minPolicyAmount')} ($)</Label>
          <input
            {...register('min_policy_amount')}
            id="min_policy_amount"
            type="number"
            step="0.01"
            min="0"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('commissions.minPolicyAmount')}
          />
        </div>
        <div>
          <Label htmlFor="max_policy_amount">{t('commissions.maxPolicyAmount')} ($)</Label>
          <input
            {...register('max_policy_amount')}
            id="max_policy_amount"
            type="number"
            step="0.01"
            min="0"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('commissions.maxPolicyAmount')}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="applies_to_new_business"
            checked={watch('applies_to_new_business')}
            onCheckedChange={(checked) => setValue('applies_to_new_business', checked)}
          />
          <Label htmlFor="applies_to_new_business">{t('commissions.appliesToNewBusiness')}</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="applies_to_renewals"
            checked={watch('applies_to_renewals')}
            onCheckedChange={(checked) => setValue('applies_to_renewals', checked)}
          />
          <Label htmlFor="applies_to_renewals">{t('commissions.appliesToRenewals')}</Label>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading || !watch('product_type')} className="bg-mg-green hover:bg-mg-green/90">
          {loading ? t('commissions.creatingRule') : t('commissions.createRule')}
        </Button>
      </div>
    </form>
  );
};

export default CommissionRuleForm;